/* eslint-disable @typescript-eslint/no-unused-vars, react-refresh/only-export-components */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  BookOpen,
  Check,
  CheckCircle2,
  Compass,
  Copy,
  FileText,
  Filter,
  Plane,
  Search,
  ShieldCheck,
  Sparkles,
  Terminal,
  Zap,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';

export interface AiAnswerHubItem {
  id: string;
  ref_code: string;
  title: string;
  category:
    | 'Sabre & RTW Rescue'
    | 'Portal & VM Automation'
    | 'Client Case Investigation'
    | 'Platform Architecture & Manual'
    | 'Decisions & Controls'
    | string;
  plain_english_summary: string;
  five_point_client_recap?: {
    who_and_relationship?: string;
    did_we_meet_or_speak?: string;
    the_situation?: string;
    discussed_and_promised?: string;
    what_needs_to_be_done_now?: string;
  } | null;
  platform_urls: Array<{ label: string; url: string }>;
  html_report_filename?: string | null;
  how_to_continue_prompt: string;
  sort_order: number;
  is_pinned: boolean;
  updated_at: string;
}

export interface SabrePeerQuestionItem {
  refCode: string;
  scenarioClass: string;
  shortTitle: string;
  questionText: string;
  whyItMatters: string;
  recommendedOptions: Array<{
    label: string;
    sabreRuleText: string;
  }>;
}

export interface SabreRescueOptionPlan {
  optionId: 'A' | 'B' | 'C';
  badge: string;
  title: string;
  subtitle: string;
  tierPointsSummary: string;
  marginImpact: string;
  highlights: string[];
  crypticBlock: string;
}

export interface UniversalBookingScenarioPreset {
  scenarioId: string;
  label: string;
  pnrLocator: string;
  customerName: string;
  customerNumber: number;
  uccTicketId: number;
  tripNumber?: number;
  detectorClasses: string[];
  loyaltyTargetSummary: string;
  financialSummary: string;
  statusSummary: string;
  fivePointRecap: {
    whoAndRelationship: string;
    didWeMeetOrSpeak: string;
    theSituation: string;
    discussedAndPromised: string;
    whatNeedsToBeDoneNow: string;
  };
}

export interface LearnedPlaybookRuleRow {
  entryKey: string;
  questionRef: string;
  scope: 'layer1_global' | 'layer2_case';
  triggerQuote: string;
  beforeBehaviour: string;
  afterRuleInstruction: string;
  verbatimRuleCitation: string;
  isActive: boolean;
}

interface PlaybookSaveReceipt {
  ok: true;
  entry_id: string;
  memory_id: string;
  question_ref: string;
  summary: string;
}

type SaveNotice = { tone: 'success' | 'error'; text: string };

function parsePlaybookSaveReceipt(data: unknown, expectedQuestionRef: string): PlaybookSaveReceipt | null {
  if (!data || typeof data !== 'object') return null;
  const value = data as Partial<PlaybookSaveReceipt>;
  if (value.ok !== true || value.question_ref !== expectedQuestionRef) return null;
  if (typeof value.entry_id !== 'string' || !value.entry_id) return null;
  if (typeof value.memory_id !== 'string' || !value.memory_id) return null;
  if (typeof value.summary !== 'string' || !value.summary) return null;
  return value as PlaybookSaveReceipt;
}

export const INITIAL_LEARNED_PLAYBOOK_RULES: LearnedPlaybookRuleRow[] = [];

// Live hub rows come only from the staff-gated RPC. Keep this export for callers that
// render an explicit example state, but never use it as a substitute for a failed read.
export const SEEDED_AI_ANSWERS_AND_REPORTS_HUB: AiAnswerHubItem[] = [];

export const SABRE_PROFESSIONAL_5_QUESTIONS: SabrePeerQuestionItem[] = [
  {
    refCode: '[SYNTHETIC-SABRE-Q1]',
    scenarioClass: 'synthetic_waitlist_resolution',
    shortTitle: 'Synthetic example · resolving a downstream waitlist before issuance',
    questionText: 'For this clearly synthetic training itinerary, should the operator keep a downstream waitlist or use the confirmed one-stop alternative?',
    whyItMatters: 'Demonstrates how a reusable rule is reviewed and saved without presenting a real customer case.',
    recommendedOptions: [
      {
        label: 'Use the confirmed alternative when the segment cap and fare rules permit it',
        sabreRuleText: '[SYNTHETIC-SABRE-Q1] Prefer a confirmed alternative over a downstream waitlist when fare rules, married segments and the segment cap have been checked.',
      },
    ],
  },
  {
    refCode: '[SYNTHETIC-SABRE-Q2]',
    scenarioClass: 'synthetic_validating_carrier',
    shortTitle: 'Synthetic example · validating-carrier comparison',
    questionText: 'Which checks must be completed before selecting a validating carrier for a synthetic RTW quote?',
    whyItMatters: 'Captures an estate-wide operating rule without embedding a live fare or traveller.',
    recommendedOptions: [
      {
        label: 'Compare fare, taxes, ticketing authority and loyalty impact before selection',
        sabreRuleText: '[SYNTHETIC-SABRE-Q2] Compare fare, taxes, ticketing authority, married-segment integrity and loyalty impact before selecting the validating carrier.',
      },
    ],
  },
];

export const STOKES_3_SABRE_RESCUE_OPTIONS: SabreRescueOptionPlan[] = [
  {
    optionId: 'A',
    badge: 'SYNTHETIC OPTION A · CONFIRMED ALTERNATIVE',
    title: 'Synthetic training option: replace the waitlisted sector with a confirmed connection',
    subtitle: 'Example only · validate live inventory and fare rules before any real action',
    tierPointsSummary: 'Illustrative loyalty comparison; no live traveller or balance is represented.',
    marginImpact: 'Illustrative commercial comparison; no live payment or margin is represented.',
    highlights: ['Checks married segments, connection time, segment cap and validating carrier before staging.'],
    crypticBlock: `*** SYNTHETIC TRAINING EXAMPLE — DO NOT EXECUTE ***\nAAA TEST\n-NAME/TRAINING EXAMPLE\n01D115JANLHRTEST¤XX100\nWPRW`,
  },
  {
    optionId: 'B',
    badge: 'SYNTHETIC OPTION B · HOLD AND MONITOR',
    title: 'Synthetic training option: retain the waitlist with a documented fallback',
    subtitle: 'Example only · requires a confirmed fallback before issuance',
    tierPointsSummary: 'Illustrative loyalty comparison only.',
    marginImpact: 'Illustrative commercial comparison only.',
    highlights: ['Records a confirmed fallback and an operator review point.'],
    crypticBlock: `*** SYNTHETIC TRAINING EXAMPLE — DO NOT EXECUTE ***\nAAA TEST\n-NAME/TRAINING EXAMPLE\n01D115JANLHRTEST¤XX200\nWPRW`,
  },
  {
    optionId: 'C',
    badge: 'SYNTHETIC OPTION C · REBUILD SHELL',
    title: 'Synthetic training option: rebuild a disrupted itinerary in a clean shell',
    subtitle: 'Example only · no live locator or customer data',
    tierPointsSummary: 'Illustrative loyalty comparison only.',
    marginImpact: 'Illustrative commercial comparison only.',
    highlights: ['Keeps the original record untouched until the replacement is independently verified.'],
    crypticBlock: `*** SYNTHETIC TRAINING EXAMPLE — DO NOT EXECUTE ***\nAAA TEST\n-NAME/TRAINING EXAMPLE\n01D115JANLHRTEST¤XX300\nWPRW`,
  },
];

export const UNIVERSAL_ESTATE_SCENARIO_PRESETS: UniversalBookingScenarioPreset[] = [
  {
    scenarioId: 'synthetic-disruption-demo',
    label: 'Synthetic training scenario · disrupted RTW itinerary (no live customer)',
    pnrLocator: 'DEMOAA',
    customerName: 'Synthetic Traveller A',
    customerNumber: 9000001,
    uccTicketId: 900001,
    tripNumber: 90001,
    detectorClasses: [
      'SYNTHETIC_LOST_INVENTORY',
      'SYNTHETIC_WAITLIST_GATE',
      'SYNTHETIC_IMMINENT_DEPARTURE',
    ],
    loyaltyTargetSummary: 'Synthetic loyalty target used only to demonstrate the comparison UI.',
    financialSummary: 'Synthetic amounts only; no live payment or margin data.',
    statusSummary: 'Training example with disrupted and waitlisted segments. Run a live evaluation for real case facts.',
    fivePointRecap: {
      whoAndRelationship: 'Synthetic Traveller A · training record only.',
      didWeMeetOrSpeak: 'No real contact; synthetic example.',
      theSituation: 'A fictional RTW itinerary contains disrupted and waitlisted sectors.',
      discussedAndPromised: 'No real promise; demonstrates the recap layout.',
      whatNeedsToBeDoneNow: 'Run the live evaluator with an authorised locator before making any operational decision.',
    },
  },
  {
    scenarioId: 'synthetic-segment-cap-demo',
    label: 'Synthetic training scenario · segment-cap review (no live customer)',
    pnrLocator: 'DEMOBB',
    customerName: 'Synthetic Traveller B',
    customerNumber: 9000002,
    uccTicketId: 900002,
    detectorClasses: ['SYNTHETIC_SEGMENT_CAP', 'SYNTHETIC_VALIDATING_CARRIER_REVIEW'],
    loyaltyTargetSummary: 'Synthetic tier target used only for UI training.',
    financialSummary: 'Synthetic quote; no live payment data.',
    statusSummary: 'Training example requiring a segment-cap and validating-carrier review.',
    fivePointRecap: {
      whoAndRelationship: 'Synthetic Traveller B · training record only.',
      didWeMeetOrSpeak: 'No real contact; synthetic example.',
      theSituation: 'A fictional itinerary exceeds the training segment threshold.',
      discussedAndPromised: 'No real promise; demonstrates the operational checklist.',
      whatNeedsToBeDoneNow: 'Use live, authorised data before creating or changing a booking.',
    },
  },
];

export interface RealityCheckCopilotPanelProps {
  defaultScenarioId?: string;
  initialSubTab?: 'sabre_rescue' | 'peer_questions' | 'answers_hub';
  onSendContinuePromptToChat?: (prompt: string) => void;
}

export const RealityCheckCopilotPanel: React.FC<RealityCheckCopilotPanelProps> = ({
  defaultScenarioId = 'synthetic-disruption-demo',
  initialSubTab = 'sabre_rescue',
  onSendContinuePromptToChat,
}) => {
  const [subTab, setSubTab] = useState<'sabre_rescue' | 'peer_questions' | 'answers_hub'>(initialSubTab);
  const [selectedScenarioId, setSelectedScenarioId] = useState<string>(defaultScenarioId);
  const [selectedStokesOption, setSelectedStokesOption] = useState<'A' | 'B' | 'C'>('A');
  const [customPnrInput, setCustomPnrInput] = useState<string>('');
  const [customCustomerInput, setCustomCustomerInput] = useState<string>('');
  const [liveEvalResult, setLiveEvalResult] = useState<Record<string, unknown> | null>(null);
  const [liveEvalError, setLiveEvalError] = useState<string | null>(null);
  const [runningLiveEval, setRunningLiveEval] = useState<boolean>(false);
  const liveEvalGeneration = useRef(0);
  const activeLiveEval = useRef<number | null>(null);

  const invalidateLiveEval = useCallback(() => {
    liveEvalGeneration.current += 1;
    activeLiveEval.current = null;
    setLiveEvalResult(null);
    setLiveEvalError(null);
    setRunningLiveEval(false);
  }, []);

  useEffect(() => () => { liveEvalGeneration.current += 1; }, []);

  // Answers & Reports Hub state
  const [hubItems, setHubItems] = useState<AiAnswerHubItem[]>([]);
  const [hubLoadError, setHubLoadError] = useState<string | null>(null);
  const [hubCoverage, setHubCoverage] = useState<'loading' | 'complete' | 'scoped_partial' | 'unknown' | 'unavailable'>('loading');
  const [hubCategory, setHubCategory] = useState<string>('All');
  const [hubSearch, setHubSearch] = useState<string>('');
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // Sabre Peer Questions state
  const [savedQuestionNotes, setSavedQuestionNotes] = useState<Record<string, string>>({});
  const [customQuestionInputs, setCustomQuestionInputs] = useState<Record<string, string>>({});

  // Real-World AI Emergency Scenario & Playbook Learning Loop state ([AGY-UPD-021])
  const [resolvedScenarioMap, setResolvedScenarioMap] = useState<Record<string, string>>(() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem('pt.realityCheck.resolvedScenarios.v1') : null;
      if (raw) {
        const parsed = JSON.parse(raw) as Record<string, string>;
        if (parsed && typeof parsed === 'object') return parsed;
      }
    } catch {
      // Ignore storage errors
    }
    return {};
  });
  const [replacementPnrInput, setReplacementPnrInput] = useState<string>('');
  const [teachRuleInput, setTeachRuleInput] = useState<string>('');
  const [learningScope, setLearningScope] = useState<'layer1_global' | 'layer2_case'>('layer1_global');
  const [learnedRules, setLearnedRules] = useState<LearnedPlaybookRuleRow[]>(() => {
    try {
      const raw = typeof window !== 'undefined' ? window.localStorage.getItem('pt.realityCheck.learnedRules.v1') : null;
      if (raw) {
        const parsed = JSON.parse(raw) as LearnedPlaybookRuleRow[];
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch {
      // Ignore storage errors
    }
    return INITIAL_LEARNED_PLAYBOOK_RULES;
  });
  const [learningBanner, setLearningBanner] = useState<SaveNotice | null>(null);
  const [pendingSaveKey, setPendingSaveKey] = useState<string | null>(null);
  const pendingSaveRef = useRef<string | null>(null);

  const activeScenario = useMemo(
    () =>
      UNIVERSAL_ESTATE_SCENARIO_PRESETS.find((s) => s.scenarioId === selectedScenarioId) ||
      UNIVERSAL_ESTATE_SCENARIO_PRESETS[0],
    [selectedScenarioId]
  );

  const savePlaybookWithReadback = useCallback(async (
    questionRef: string,
    answerText: string,
    scenarioClass: string,
    customerNumber: number | null,
  ): Promise<PlaybookSaveReceipt> => {
    const { data, error } = await supabase.rpc('ops_save_sabre_playbook_answer_v1' as never, {
      p_question_ref: questionRef,
      p_answer_text: answerText,
      p_scenario_class: scenarioClass,
      p_applies_to_customer_number: customerNumber,
    } as never);
    if (error) throw error;
    const receipt = parsePlaybookSaveReceipt(data, questionRef);
    if (!receipt) throw new Error('Playbook save did not return a complete receipt');

    const { data: context, error: readError } = await supabase.rpc(
      'dk_management_assistant_context_v1' as never,
      { p_query: answerText } as never,
    );
    if (readError) throw readError;
    const memories = (context as { memories?: Array<{ id?: string; instruction_text?: string }> } | null)?.memories;
    const savedMemory = Array.isArray(memories)
      ? memories.find((memory) => memory.id === receipt.memory_id)
      : undefined;
    if (!savedMemory || savedMemory.instruction_text !== answerText) {
      throw new Error('Saved playbook entry could not be read back');
    }
    return receipt;
  }, []);

  const persistScenarioResolution = useCallback(
    async (scenarioId: string, resolutionLabel: string, replacementPnr?: string) => {
      if (pendingSaveRef.current) return;
      const saveKey = `scenario:${scenarioId}`;
      const questionRef = `SCENARIO-RESOLVED-${scenarioId.toUpperCase()}`;
      const answerText = replacementPnr
        ? `${resolutionLabel} (Replacement PNR: ${replacementPnr})`
        : resolutionLabel;
      pendingSaveRef.current = saveKey;
      setPendingSaveKey(saveKey);
      setLearningBanner(null);
      try {
        await savePlaybookWithReadback(questionRef, answerText, 'SCENARIO_RESOLUTION', activeScenario.customerNumber);
        setResolvedScenarioMap((prev) => {
          const next = { ...prev, [scenarioId]: `${resolutionLabel} · Local browser dismissal` };
          try {
            window.localStorage.setItem('pt.realityCheck.resolvedScenarios.v1', JSON.stringify(next));
          } catch {
            // The server receipt remains authoritative even if this browser cannot retain its dismissal.
          }
          return next;
        });
        if (replacementPnr) setReplacementPnrInput('');
        setLearningBanner({
          tone: 'success',
          text: 'Playbook note saved and read back. The resolved badge is local to this browser; other screens continue to use live booking state.',
        });
      } catch {
        setLearningBanner({
          tone: 'error',
          text: 'Resolution save not confirmed. No resolved badge was added; check the playbook before trying again.',
        });
      } finally {
        pendingSaveRef.current = null;
        setPendingSaveKey(null);
      }
    },
    [activeScenario.customerNumber, savePlaybookWithReadback]
  );

  const persistLearnedPlaybookRule = useCallback(
    async (text: string, scope: 'layer1_global' | 'layer2_case') => {
      if (pendingSaveRef.current) return;
      const newRef = `[RULE-LEARNED-0${learnedRules.length + 1}]`;
      const newRow: LearnedPlaybookRuleRow = {
        entryKey: `custom_learned_rule_${Date.now()}`,
        questionRef: newRef,
        scope,
        triggerQuote: text,
        beforeBehaviour: 'Previous default heuristic before Daniel correction.',
        afterRuleInstruction: text,
        verbatimRuleCitation:
          scope === 'layer1_global'
            ? 'Layer 1 Global Playbook (`public.dk_assistant_knowledge_entries`)'
            : `Layer 2 Case Memory (#${activeScenario.customerNumber})`,
        isActive: true,
      };
      pendingSaveRef.current = 'learned-rule';
      setPendingSaveKey('learned-rule');
      setLearningBanner(null);
      try {
        await savePlaybookWithReadback(
          newRef,
          text,
          scope,
          scope === 'layer2_case' ? activeScenario.customerNumber : null,
        );
        setLearnedRules((prev) => {
          const next = [newRow, ...prev];
          try {
            window.localStorage.setItem('pt.realityCheck.learnedRules.v1', JSON.stringify(next));
          } catch {
            // The read-back server record remains authoritative.
          }
          return next;
        });
        setTeachRuleInput('');
        setLearningBanner({ tone: 'success', text: `Saved and read back ${newRef}.` });
      } catch {
        setLearningBanner({
          tone: 'error',
          text: 'Rule save not confirmed. Your text is retained; check the playbook before trying again.',
        });
      } finally {
        pendingSaveRef.current = null;
        setPendingSaveKey(null);
      }
    },
    [activeScenario.customerNumber, learnedRules.length, savePlaybookWithReadback]
  );

  const activeStokesPlan = useMemo(
    () =>
      STOKES_3_SABRE_RESCUE_OPTIONS.find((o) => o.optionId === selectedStokesOption) ||
      STOKES_3_SABRE_RESCUE_OPTIONS[0],
    [selectedStokesOption]
  );

  const loadAnswersHubFromDb = useCallback(async () => {
    setHubLoadError(null);
    try {
      const { data, error } = await supabase.rpc(
        'ops_ai_answers_and_reports_list_v1' as never,
        { p_category: null, p_search: null } as never
      );
      const res = data as { ok?: boolean; items?: AiAnswerHubItem[]; source_coverage?: { status?: string } } | null;
      if (error || res?.ok !== true || !Array.isArray(res.items)) throw error || new Error('Incomplete hub response');
      setHubItems(res.items);
      setHubCoverage(res.source_coverage?.status === 'complete' || res.source_coverage?.status === 'scoped_partial'
        ? res.source_coverage.status : 'unknown');
    } catch {
      setHubItems([]);
      setHubCoverage('unavailable');
      setHubLoadError('Answers hub unavailable. No example rows are being shown as live records.');
    }
  }, []);

  useEffect(() => {
    void loadAnswersHubFromDb();
  }, [loadAnswersHubFromDb]);

  const handleRunUniversalEval = useCallback(async (locator: string, customerInput: string) => {
    const normalizedLocator = locator.trim().replace(/^1S-/i, '').toUpperCase();
    const customerText = customerInput.trim();
    const customerNumber = Number(customerText);
    if (activeLiveEval.current !== null) return;
    if (!/^[A-Z0-9]{6}$/.test(normalizedLocator) || !/^[1-9]\d*$/.test(customerText)
      || !Number.isSafeInteger(customerNumber) || customerNumber > 2147483647) {
      setLiveEvalResult(null);
      setLiveEvalError('Enter a customer number and a six-character PNR locator for that customer.');
      return;
    }
    const generation = ++liveEvalGeneration.current;
    activeLiveEval.current = generation;
    setRunningLiveEval(true);
    setLiveEvalResult(null);
    setLiveEvalError(null);
    try {
      const { data, error } = await supabase.rpc(
        'ops_evaluate_booking_troubleshooting_v1' as never,
        {
          p_pnr_locator: normalizedLocator,
          p_customer_number: customerNumber,
        } as never
      );
      if (error || !data || typeof data !== 'object') throw error || new Error('Incomplete evaluator response');
      const result = data as Record<string, unknown>;
      if (result.ok !== true || result.case_context_status !== 'exact_customer_and_pnr'
        || result.customer_number !== customerNumber || result.pnr_locator !== normalizedLocator
        || typeof result.passenger_name !== 'string' || !result.passenger_name.trim()) {
        throw new Error('No matching live PNR record was returned');
      }
      if (generation === liveEvalGeneration.current) setLiveEvalResult(result);
    } catch {
      if (generation === liveEvalGeneration.current) {
        setLiveEvalResult(null);
        setLiveEvalError('Live evaluation not confirmed. Check that the customer and PNR match, that you can access the customer, and that the source record is unambiguous.');
      }
    } finally {
      if (generation === liveEvalGeneration.current) {
        activeLiveEval.current = null;
        setRunningLiveEval(false);
      }
    }
  }, []);

  const handleCopyText = useCallback(async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard fallback
    }
    setCopiedCode(key);
    setTimeout(() => setCopiedCode(null), 3000);
  }, []);

  const handleSavePeerQuestionAnswer = useCallback(
    async (q: SabrePeerQuestionItem, answerText: string) => {
      const trimmed = answerText.trim();
      if (!trimmed || pendingSaveRef.current) return;
      pendingSaveRef.current = `peer:${q.refCode}`;
      setPendingSaveKey(`peer:${q.refCode}`);
      setSavedQuestionNotes((prev) => ({ ...prev, [q.refCode]: 'Saving and checking…' }));
      try {
        await savePlaybookWithReadback(q.refCode, trimmed, q.scenarioClass, activeScenario.customerNumber);
        setSavedQuestionNotes((prev) => ({ ...prev, [q.refCode]: `Saved and read back (${q.refCode}).` }));
        setCustomQuestionInputs((prev) => ({ ...prev, [q.refCode]: '' }));
      } catch {
        setSavedQuestionNotes((prev) => ({
          ...prev,
          [q.refCode]: 'Save not confirmed. Your custom answer is retained; check the playbook before retrying.',
        }));
      } finally {
        pendingSaveRef.current = null;
        setPendingSaveKey(null);
      }
    },
    [activeScenario.customerNumber, savePlaybookWithReadback]
  );

  const filteredHubItems = useMemo(() => {
    const q = hubSearch.trim().toLowerCase();
    return hubItems.filter((item) => {
      if (hubCategory !== 'All' && item.category !== hubCategory) return false;
      if (!q) return true;
      return (
        item.ref_code.toLowerCase().includes(q) ||
        item.title.toLowerCase().includes(q) ||
        item.plain_english_summary.toLowerCase().includes(q) ||
        item.how_to_continue_prompt.toLowerCase().includes(q)
      );
    });
  }, [hubItems, hubCategory, hubSearch]);

  return (
    <div
      data-testid="reality-check-copilot-panel"
      className="rounded-2xl border-2 border-indigo-300 bg-white shadow-md overflow-hidden text-xs"
    >
      {/* Top Architecture Banner: Universal Engine + Dedicated HTML Report Link */}
      <div className="bg-gradient-to-r from-slate-950 via-indigo-950 to-purple-950 px-4 py-3 text-white flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-0.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-400/20 border border-amber-300/50 px-2.5 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-amber-200">
              <Sparkles className="h-3 w-3" />
              Universal Sabre Troubleshooting, Codeshare Maximiser &amp; AI Reference Hub
            </span>
            <span className="rounded bg-emerald-500/20 border border-emerald-400/40 px-2 py-0.5 font-mono text-[10px] font-bold text-emerald-200">
              Estate-Wide Dynamic Engine · Never Hardcoded to 1 Client
            </span>
          </div>
          <p className="text-[11px] text-indigo-100/90">
            Evaluates any booking across 5 Universal Detectors, maximises Fab-4 (`BA*`/`AA*`/`IB*`/`AY*`) codeshares (`‡UI*`) toward each client&apos;s loyalty goal, asks peer-level Sabre Professional questions, and indexes every answer/report with `[REF-*]` codes.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span
            role="status"
            data-testid="dedicated-html-report-unavailable"
            className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-bold text-amber-950"
          >
            <FileText className="h-3.5 w-3.5" />
            Archived case dossier unavailable here; use the authorised case workspace.
          </span>
        </div>
      </div>

      {/* Sub-Navigation Bar */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-100 px-4 py-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            data-testid="rc-tab-sabre-rescue"
            onClick={() => setSubTab('sabre_rescue')}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition ${
              subTab === 'sabre_rescue'
                ? 'bg-indigo-950 text-white shadow-2xs'
                : 'bg-white text-slate-700 hover:bg-slate-200/70'
            }`}
          >
            <Plane className="h-3.5 w-3.5" />
            1. Universal Scenario Detector &amp; 3 Synthetic Training Options
          </button>
          <button
            type="button"
            data-testid="rc-tab-peer-questions"
            onClick={() => setSubTab('peer_questions')}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition ${
              subTab === 'peer_questions'
                ? 'bg-purple-900 text-white shadow-2xs'
                : 'bg-purple-50 text-purple-950 border border-purple-200 hover:bg-purple-100'
            }`}
          >
            <Terminal className="h-3.5 w-3.5" />
            2. 5 Peer-to-Peer Sabre Professional Questions ([REF-ATH-STOKES-Q1..Q5])
          </button>
          <button
            type="button"
            data-testid="rc-tab-answers-hub"
            onClick={() => setSubTab('answers_hub')}
            className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition ${
              subTab === 'answers_hub'
                ? 'bg-emerald-800 text-white shadow-2xs'
                : 'bg-emerald-50 text-emerald-950 border border-emerald-200 hover:bg-emerald-100'
            }`}
          >
            <BookOpen className="h-3.5 w-3.5" />
            3. Accessible AI Answers, Reports &amp; Follow-Up Reference Index ({hubItems.length} [REF-*] Cards)
          </button>
        </div>

        {/* Universal Scenario Switcher */}
        <div className="flex flex-wrap items-center gap-1.5">
          <label htmlFor="universal-scenario-select" className="text-[11px] font-bold text-slate-700">
            Active Booking Scenario:
          </label>
          <select
            id="universal-scenario-select"
            data-testid="universal-scenario-selector"
            value={selectedScenarioId}
            onChange={(e) => {
              const nextId = e.target.value;
              setSelectedScenarioId(nextId);
              invalidateLiveEval();
            }}
            className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-bold text-slate-900"
          >
            {UNIVERSAL_ESTATE_SCENARIO_PRESETS.map((s) => (
              <option key={s.scenarioId} value={s.scenarioId}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* SUB-TAB 1: UNIVERSAL SCENARIO DETECTOR & 3 SABRE RESCUE OPTIONS */}
      {subTab === 'sabre_rescue' && (
        <div className="p-4 space-y-4">
          {/* Universal Scenario Header + Dynamic PNR Inspector Bar */}
          <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-3.5 space-y-2.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-sm font-extrabold text-indigo-950 flex flex-wrap items-center gap-2">
                  <span>{activeScenario.customerName} (#{activeScenario.customerNumber})</span>
                  <span className="rounded bg-slate-900 px-2 py-0.5 font-mono text-xs text-amber-300">
                    PNR 1S-{activeScenario.pnrLocator}
                  </span>
                   <span className="rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-950">
                     Synthetic training preset — no linked live record
                   </span>
                </div>
                <p className="text-[11px] text-slate-700 mt-1">
                  <strong>Financial &amp; Ticketing Reality:</strong> {activeScenario.financialSummary} ·{' '}
                  <strong>Loyalty Target:</strong> {activeScenario.loyaltyTargetSummary}
                </p>
              </div>

              {/* Dynamic Any-PNR Live Evaluator */}
              <div className="flex flex-wrap items-end gap-1.5">
                <label className="grid gap-1 text-[11px] font-semibold text-slate-700">
                  Customer number
                  <input
                    type="text"
                    inputMode="numeric"
                    value={customCustomerInput}
                    onChange={(e) => { setCustomCustomerInput(e.target.value); invalidateLiveEval(); }}
                    placeholder="Customer #"
                    className="w-32 rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-mono"
                  />
                </label>
                <label className="grid gap-1 text-[11px] font-semibold text-slate-700">
                  PNR locator
                <input
                  type="text"
                  value={customPnrInput}
                  onChange={(e) => { setCustomPnrInput(e.target.value); invalidateLiveEval(); }}
                  placeholder="Inspect an authorised PNR locator…"
                  className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-mono uppercase w-48"
                />
                </label>
                <button
                  type="button"
                  disabled={runningLiveEval || !customPnrInput.trim() || !customCustomerInput.trim()}
                  onClick={() =>
                    void handleRunUniversalEval(
                      customPnrInput,
                      customCustomerInput
                    )
                  }
                  className="inline-flex items-center gap-1 rounded-lg bg-[#005eb8] hover:bg-[#004b93] px-3 py-1 text-xs font-bold text-white disabled:opacity-50"
                >
                  <Compass className="h-3.5 w-3.5" />
                  {runningLiveEval ? 'Scanning…' : 'Run Universal Detector'}
                </button>
              </div>
            </div>

            {/* Triggered Universal Detectors Strip */}
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-600">
                Triggered Universal Detectors:
              </span>
              {activeScenario.detectorClasses.map((det, idx) => (
                <span
                  key={idx}
                  className="rounded-md border border-red-300 bg-red-50 px-2 py-0.5 font-mono text-[10px] font-bold text-red-900"
                >
                  {det}
                </span>
              ))}
            </div>

            {/* 5-Point Memory Recap Box */}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-5 rounded-lg border border-indigo-200 bg-white p-2.5 text-[11px]">
              <div>
                <div className="font-extrabold text-indigo-950">1. Who &amp; Relationship</div>
                <p className="text-slate-700 mt-0.5">{activeScenario.fivePointRecap.whoAndRelationship}</p>
              </div>
              <div>
                <div className="font-extrabold text-indigo-950">2. Did We Meet / Speak?</div>
                <p className="text-slate-700 mt-0.5">{activeScenario.fivePointRecap.didWeMeetOrSpeak}</p>
              </div>
              <div>
                <div className="font-extrabold text-indigo-950">3. The Situation</div>
                <p className="text-slate-700 mt-0.5">{activeScenario.fivePointRecap.theSituation}</p>
              </div>
              <div>
                <div className="font-extrabold text-indigo-950">4. Discussed &amp; Promised</div>
                <p className="text-slate-700 mt-0.5">{activeScenario.fivePointRecap.discussedAndPromised}</p>
              </div>
              <div>
                <div className="font-extrabold text-rose-900">5. Action Right Now</div>
                <p className="text-slate-900 font-semibold mt-0.5">
                  {activeScenario.fivePointRecap.whatNeedsToBeDoneNow}
                </p>
              </div>
            </div>

            {liveEvalResult && (
              <div role="status" data-testid="booking-evaluator-context-status" className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-[11px] text-slate-900">
                <strong>Matched customer #{String(liveEvalResult.customer_number)} · PNR {String(liveEvalResult.pnr_locator)}</strong>
                <span> · {String(liveEvalResult.passenger_name)}. Record association verified; fare, ticketing and payment readiness require their own evidence.</span>
              </div>
            )}
            {liveEvalError && (
              <div role="alert" className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] font-semibold text-amber-950">
                {liveEvalError}
              </div>
            )}
          </div>

          {/* 4-Step Real-World AI Emergency Scenario, In-Card Decision Questions & Playbook Learning Loop ([REF-AI-EMERGENCY-LEARNING-UI] · [AGY-UPD-021]) */}
          <div
            data-testid="ai-emergency-learning-loop-card"
            className="rounded-xl border-2 border-emerald-300 bg-emerald-50/40 p-3.5 space-y-3"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-xs font-extrabold uppercase tracking-wider text-emerald-950 flex items-center gap-1.5">
                  <ShieldCheck className="h-4 w-4 text-emerald-700" />
                  Real-World AI Scenario Handler, 1-Click &ldquo;PNR Already Created&rdquo; Dismissal &amp; Continuous Playbook Learning (`[REF-AI-EMERGENCY-LEARNING-UI]`)
                </div>
                <p className="text-[11px] text-slate-700 mt-0.5">
                  For a real case, run the live evaluator first. Mark Done stores a verified playbook note and applies a clearly local browser dismissal; it does not change booking state elsewhere.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {resolvedScenarioMap[activeScenario.scenarioId] ? (
                  <span
                    data-testid="ai-emergency-scenario-resolved-badge"
                    className="inline-flex items-center gap-1 rounded-lg bg-emerald-700 px-3 py-1.5 text-xs font-extrabold text-white"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {resolvedScenarioMap[activeScenario.scenarioId]}
                  </span>
                ) : (
                  <button
                    type="button"
                    data-testid="ai-emergency-mark-pnr-created-btn"
                    onClick={() => {
                      const rep = replacementPnrInput.trim().toUpperCase();
                      const msg = rep
                        ? `PNR Recreated (${rep}) — Marked Done ✓`
                        : '✓ I Already Created / Fixed PNR in Sabre — Marked Done';
                      void persistScenarioResolution(activeScenario.scenarioId, msg, rep || undefined);
                    }}
                    disabled={pendingSaveKey !== null}
                    className="inline-flex items-center gap-1 rounded-lg bg-emerald-700 hover:bg-emerald-800 px-3 py-1.5 text-xs font-extrabold text-white"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    ✓ I Already Created / Fixed PNR in Sabre (Mark Done)
                  </button>
                )}

                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    aria-label="Replacement PNR Locator"
                    value={replacementPnrInput}
                    onChange={(e) => setReplacementPnrInput(e.target.value)}
                    placeholder="New PNR (optional)…"
                    className="rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs font-mono uppercase w-36"
                  />
                  <button
                    type="button"
                    data-testid="ai-emergency-link-replacement-pnr-btn"
                    onClick={() => {
                      const loc = replacementPnrInput.trim().toUpperCase();
                      if (!loc) return;
                      const msg = `Linked Replacement PNR 1S-${loc} — Resolved ✓`;
                      void persistScenarioResolution(activeScenario.scenarioId, msg, loc);
                    }}
                    disabled={pendingSaveKey !== null || !replacementPnrInput.trim()}
                    className="rounded-lg border border-slate-300 bg-white hover:bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-800"
                  >
                    🔗 Link Replacement PNR
                  </button>
                </div>
              </div>
            </div>

            {learningBanner && (
              <div
                data-testid="ai-emergency-learning-banner"
                role={learningBanner.tone === 'error' ? 'alert' : 'status'}
                className={`rounded-lg border px-3 py-2 text-xs font-bold flex items-center justify-between ${learningBanner.tone === 'success' ? 'border-emerald-400 bg-emerald-100 text-emerald-950' : 'border-amber-300 bg-amber-50 text-amber-950'}`}
              >
                <span>{learningBanner.text}</span>
                <button
                  type="button"
                  onClick={() => setLearningBanner(null)}
                  className="text-[11px] underline"
                >
                  Dismiss
                </button>
              </div>
            )}

            {/* Instant Feedback & Rule-Correction Input Bar */}
            <div
              data-testid="ai-emergency-teach-correct-bar"
              className="rounded-xl border border-indigo-200 bg-white p-3 space-y-2"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-extrabold text-indigo-950 text-xs">
                  🧠 Teach or Correct the Assistant in Real Time (Converts Your Feedback into a Reusable Rule):
                </span>
                <div className="flex items-center gap-3 text-[11px]">
                  <label className="inline-flex items-center gap-1 font-bold text-emerald-900 cursor-pointer">
                    <input
                      type="radio"
                      name="ai-learning-scope"
                      data-testid="ai-learning-scope-layer1"
                      checked={learningScope === 'layer1_global'}
                      onChange={() => setLearningScope('layer1_global')}
                    />
                    🌐 Layer 1 Global Playbook Rule (All Future Bookings)
                  </label>
                  <label className="inline-flex items-center gap-1 font-semibold text-slate-700 cursor-pointer">
                    <input
                      type="radio"
                      name="ai-learning-scope"
                      data-testid="ai-learning-scope-layer2"
                      checked={learningScope === 'layer2_case'}
                      onChange={() => setLearningScope('layer2_case')}
                    />
                    📌 Layer 2 Case Memory Only ({activeScenario.customerName} #{activeScenario.customerNumber})
                  </label>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="text"
                  data-testid="ai-emergency-teach-input"
                  value={teachRuleInput}
                  onChange={(e) => setTeachRuleInput(e.target.value)}
                  placeholder="e.g. WL is allowed on every segment except Seg 1 (Rule 3015 Cat 05), or always test married O&D SYD-HKG-LHR on CX first…"
                  className="flex-1 min-w-[260px] rounded-lg border border-slate-300 px-3 py-1.5 text-xs"
                />
                <button
                  type="button"
                  data-testid="ai-learning-save-rule-btn"
                  onClick={() => {
                    const text = teachRuleInput.trim();
                    if (!text) return;
                    void persistLearnedPlaybookRule(text, learningScope);
                  }}
                  disabled={pendingSaveKey !== null}
                  className="rounded-lg bg-indigo-900 hover:bg-indigo-800 px-3.5 py-1.5 text-xs font-extrabold text-white"
                >
                  💾 Save Feedback &amp; Teach AI
                </button>
              </div>
            </div>

            {/* Learned Playbook Rules Ledger (Before vs After Audit Trail + Revert Controls) */}
            <div
              data-testid="ai-learned-playbook-rules-ledger"
              className="rounded-xl border border-slate-200 bg-white p-3 space-y-2"
            >
              <div className="text-xs font-extrabold text-slate-900">
                📖 Active Learned Playbook Rules (`public.dk_assistant_knowledge_entries` · Before vs After Audit Trail):
              </div>
              <div className="space-y-1.5">
                {learnedRules.map((rule) => (
                  <div
                    key={rule.entryKey}
                    className={`rounded-lg border px-3 py-2 text-[11px] flex flex-wrap items-start justify-between gap-2 ${
                      rule.isActive
                        ? 'border-emerald-200 bg-emerald-50/40'
                        : 'border-slate-200 bg-slate-100 opacity-60'
                    }`}
                  >
                    <div className="space-y-0.5 max-w-3xl">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="font-mono font-extrabold text-indigo-900">
                          {rule.questionRef}
                        </span>
                        <span className="rounded bg-emerald-100 border border-emerald-300 px-1.5 py-0.5 text-[10px] font-bold text-emerald-950">
                          {rule.scope === 'layer1_global'
                            ? '🌐 Layer 1 Global Rule'
                            : '📌 Layer 2 Case Rule'}
                        </span>
                        <span className="font-mono text-[10px] text-slate-600">
                          {rule.verbatimRuleCitation}
                        </span>
                      </div>
                      <div className="text-slate-600">
                        <strong>Before:</strong> {rule.beforeBehaviour}
                      </div>
                      <div className="text-slate-900 font-semibold">
                        <strong>After (Active Rule):</strong> {rule.afterRuleInstruction}
                      </div>
                    </div>
                    <button
                      type="button"
                      data-testid={`toggle-learned-rule-${rule.questionRef}`}
                      onClick={() =>
                        setLearnedRules((prev) =>
                          prev.map((r) =>
                            r.entryKey === rule.entryKey ? { ...r, isActive: !r.isActive } : r
                          )
                        )
                      }
                      className="rounded border border-slate-300 bg-white hover:bg-slate-50 px-2 py-1 text-[10px] font-bold text-slate-700"
                    >
                      {rule.isActive ? '↩️ Revert / Disable' : '✓ Re-Enable Rule'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Clearly synthetic options; never present these as live inventory. */}
          <div className="rounded-xl border-2 border-purple-300 bg-purple-50/40 p-3.5 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-xs font-extrabold uppercase tracking-wider text-purple-950 flex items-center gap-1.5">
                  <Terminal className="h-4 w-4 text-purple-700" />
                  3 Synthetic Sabre Training Options (No Live Customer or Inventory)
                </div>
                <p className="text-[11px] text-slate-700 mt-0.5">
                  Use these examples to inspect the workflow only. Run live authorised checks before copying or executing any real command.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-1.5">
                {STOKES_3_SABRE_RESCUE_OPTIONS.map((opt) => (
                  <button
                    key={opt.optionId}
                    type="button"
                    data-testid={`stokes-sabre-option-${opt.optionId}`}
                    onClick={() => setSelectedStokesOption(opt.optionId)}
                    className={`rounded-lg px-3 py-1.5 text-xs font-extrabold transition ${
                      selectedStokesOption === opt.optionId
                        ? 'bg-purple-900 text-white shadow-2xs'
                        : 'bg-white text-purple-950 border border-purple-300 hover:bg-purple-100'
                    }`}
                  >
                    Option {opt.optionId}
                  </button>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-purple-200 bg-white p-3.5 space-y-2.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="rounded-full bg-purple-100 border border-purple-300 px-2.5 py-0.5 text-[10px] font-extrabold text-purple-950">
                  {activeStokesPlan.badge}
                </span>
                <span className="rounded-full bg-emerald-100 border border-emerald-300 px-2.5 py-0.5 text-[10px] font-extrabold text-emerald-950">
                  {activeStokesPlan.tierPointsSummary}
                </span>
              </div>

              <div className="text-sm font-extrabold text-slate-900">{activeStokesPlan.title}</div>
              <div className="text-xs font-semibold text-indigo-900">{activeStokesPlan.subtitle}</div>
              <div className="text-xs font-bold text-emerald-900">
                💰 Commercial Margin Protection: {activeStokesPlan.marginImpact}
              </div>

              <ul className="space-y-1 pl-4 list-disc text-slate-700 text-[11px]">
                {activeStokesPlan.highlights.map((h, i) => (
                  <li key={i}>{h}</li>
                ))}
              </ul>

              <div className="rounded-lg border border-slate-800 bg-slate-950 p-3 text-slate-100 space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-mono text-[11px] font-bold text-amber-300">
                    Synthetic Sabre Training Block — Option {activeStokesPlan.optionId} (BN5K via DW9L)
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      data-testid={`copy-stokes-option-${activeStokesPlan.optionId}-btn`}
                      onClick={() =>
                        void handleCopyText(`opt-${activeStokesPlan.optionId}`, activeStokesPlan.crypticBlock)
                      }
                      className="inline-flex items-center gap-1 rounded-md bg-amber-400 hover:bg-amber-300 px-2.5 py-1 text-[11px] font-extrabold text-slate-950"
                    >
                      {copiedCode === `opt-${activeStokesPlan.optionId}` ? (
                        <>
                          <Check className="h-3.5 w-3.5" />
                          Copied Option {activeStokesPlan.optionId} Block!
                        </>
                      ) : (
                        <>
                          <Copy className="h-3.5 w-3.5" />
                          Copy Synthetic Option {activeStokesPlan.optionId}
                        </>
                      )}
                    </button>
                    {onSendContinuePromptToChat && (
                      <button
                        type="button"
                        onClick={() =>
                          onSendContinuePromptToChat(
                            `Continue [SYNTHETIC-SABRE-TRAINING]: Explain Option ${activeStokesPlan.optionId}; do not execute it on a live booking.`
                          )
                        }
                        className="inline-flex items-center gap-1 rounded-md bg-indigo-600 hover:bg-indigo-500 px-2.5 py-1 text-[11px] font-bold text-white"
                      >
                        <Zap className="h-3.5 w-3.5" />
                        Use Option {activeStokesPlan.optionId} in Chat
                      </button>
                    )}
                  </div>
                </div>
                <pre className="overflow-x-auto rounded bg-slate-900 p-2.5 font-mono text-[11px] text-emerald-300 leading-relaxed">
                  {activeStokesPlan.crypticBlock}
                </pre>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* SUB-TAB 2: 5 PEER-TO-PEER SABRE PROFESSIONAL QUESTIONS ([REF-ATH-STOKES-Q1..Q5]) */}
      {subTab === 'peer_questions' && (
        <div className="p-4 space-y-3" data-testid="sabre-peer-questions-list">
          <div className="rounded-xl border border-purple-300 bg-purple-50/70 p-3 flex flex-wrap items-center justify-between gap-2">
            <div>
              <div className="text-sm font-extrabold text-purple-950 flex items-center gap-1.5">
                <Sparkles className="h-4 w-4 text-amber-500" />
                Synthetic Sabre Playbook Questions (`[REF-SABRE-TRAINING-Q1..]`)
              </div>
              <p className="text-[11px] text-slate-700 mt-0.5">
                Click a suggested technique or type your own instruction. A rule is shown as saved only after the server receipt and memory readback both succeed.
              </p>
            </div>
            <button
              type="button"
              onClick={() =>
                void handleCopyText(
                  'all-q-prompt',
                  'Continue [REF-SABRE-TRAINING-QA]: review these synthetic playbook answers. Do not execute a command without an authorised live booking and fresh inventory evidence.'
                )
              }
              className="inline-flex items-center gap-1 rounded-lg bg-purple-900 hover:bg-purple-800 px-3 py-1.5 text-xs font-bold text-white"
            >
              <Copy className="h-3.5 w-3.5" />
              {copiedCode === 'all-q-prompt' ? 'Copied All Q1–Q5 Reply Prompt!' : 'Copy 1-Line Reply for All Q1–Q5'}
            </button>
          </div>

          <div className="space-y-3">
            {SABRE_PROFESSIONAL_5_QUESTIONS.map((q) => (
              <div
                key={q.refCode}
                data-testid={`sabre-pro-question-${q.refCode}`}
                className="rounded-xl border border-slate-200 bg-white p-3.5 space-y-2 shadow-2xs"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="rounded bg-indigo-950 px-2 py-0.5 font-mono text-[11px] font-bold text-amber-300">
                      {q.refCode}
                    </span>
                    <span className="font-extrabold text-slate-900 text-xs">{q.shortTitle}</span>
                  </div>
                  <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-[10px] font-semibold text-slate-700">
                    Scenario Class: {q.scenarioClass}
                  </span>
                </div>

                <p className="text-slate-800 leading-relaxed">{q.questionText}</p>
                <p className="text-[11px] text-emerald-900 font-semibold">
                  <strong>Why This Matters Across All Bookings:</strong> {q.whyItMatters}
                </p>

                <div className="space-y-1.5 pt-1">
                  {q.recommendedOptions.map((opt, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => void handleSavePeerQuestionAnswer(q, opt.sabreRuleText)}
                      disabled={pendingSaveKey !== null}
                      className="w-full text-left rounded-lg border border-indigo-200 bg-indigo-50/70 hover:bg-indigo-100 px-3 py-1.5 text-[11px] font-semibold text-indigo-950 transition flex items-center justify-between gap-2"
                    >
                      <span>⚡ {opt.label}</span>
                      <span className="shrink-0 rounded bg-indigo-900 px-2 py-0.5 text-[10px] font-bold text-white">
                        Save Rule
                      </span>
                    </button>
                  ))}
                </div>

                <div className="flex flex-wrap items-center gap-2 pt-1">
                  <input
                    type="text"
                    value={customQuestionInputs[q.refCode] || ''}
                    onChange={(e) =>
                      setCustomQuestionInputs((prev) => ({ ...prev, [q.refCode]: e.target.value }))
                    }
                    placeholder={`Teach your custom Sabre rule for ${q.refCode}…`}
                    className="flex-1 min-w-[220px] rounded-md border border-slate-300 px-2.5 py-1.5 text-xs"
                  />
                  <button
                    type="button"
                    onClick={() =>
                      void handleSavePeerQuestionAnswer(q, customQuestionInputs[q.refCode] || '')
                    }
                    disabled={pendingSaveKey !== null}
                    className="rounded-md bg-slate-900 hover:bg-slate-800 px-3 py-1.5 text-xs font-bold text-white"
                  >
                    Save Custom Rule
                  </button>
                </div>

                {savedQuestionNotes[q.refCode] && (
                  <div className="rounded border border-emerald-300 bg-emerald-50 px-2.5 py-1.5 text-[11px] font-bold text-emerald-950 flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
                    <span>{savedQuestionNotes[q.refCode]}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SUB-TAB 3: ALL AI ANSWERS, REPORTS & FOLLOW-UP REFERENCE INDEX ([REF-*]) */}
      {subTab === 'answers_hub' && (
        <div className="p-4 space-y-3" data-testid="ai-answers-and-reports-hub-tab">
          {hubLoadError && <p role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs font-semibold text-amber-950">{hubLoadError}</p>}
          {!hubLoadError && hubCoverage !== 'complete' && (
            <p role="status" data-testid="ai-answers-hub-coverage" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs font-semibold text-amber-950">
              {hubCoverage === 'scoped_partial'
                ? 'Showing accessible reference entries only. Private or unverified entries are withheld.'
                : hubCoverage === 'loading'
                  ? 'Checking available reference entries…'
                  : 'Reference coverage could not be confirmed. Only returned entries are shown.'}
            </p>
          )}
          <p data-testid="ai-answers-report-availability" className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-xs text-slate-700">
            Archived HTML case reports are unavailable from this workspace. Current case evidence remains in the authorised case workspace.
          </p>
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50/70 p-3">
            <div>
              <div className="text-sm font-extrabold text-emerald-950 flex items-center gap-1.5">
                <BookOpen className="h-4 w-4 text-emerald-700" />
                Permanent In-Platform &ldquo;Accessible AI Answers, Reports &amp; Follow-Up Reference Hub (`[REF-*]`)&rdquo;
              </div>
              <p className="text-[11px] text-slate-700 mt-0.5">
                Available answers and reports appear below with their plain-English summary, 5-Point Client Recap, platform links, and a 1-click **`Continue [REF-*]`** prompt you can paste into chat anytime.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  data-testid="ai-answers-hub-search"
                  value={hubSearch}
                  onChange={(e) => setHubSearch(e.target.value)}
                  placeholder="Filter live reference rows…"
                  className="rounded-lg border border-slate-300 bg-white pl-8 pr-3 py-1 text-xs w-64"
                />
              </div>
              <select
                aria-label="Filter AI Answers by Category"
                value={hubCategory}
                onChange={(e) => setHubCategory(e.target.value)}
                className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold"
              >
                <option value="All">All Categories ({hubItems.length})</option>
                <option value="Sabre & RTW Rescue">Sabre &amp; RTW Rescue</option>
                <option value="Client Case Investigation">Client Case Investigation</option>
                <option value="Portal & VM Automation">Portal &amp; VM Automation</option>
                <option value="Platform Architecture & Manual">Platform Architecture &amp; Manual</option>
                <option value="Decisions & Controls">Decisions &amp; Controls</option>
              </select>
            </div>
          </div>

          <div className="space-y-3">
            {filteredHubItems.map((item) => (
              <div
                key={item.ref_code}
                data-testid={`ai-answer-card-${(item.ref_code || '').replace(/[^A-Z0-9-]/gi, '')}`}
                className="rounded-xl border border-slate-200 bg-white p-3.5 space-y-2.5 shadow-2xs"
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="space-y-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="rounded-md bg-slate-900 px-2.5 py-0.5 font-mono text-xs font-extrabold text-amber-300">
                        {item.ref_code}
                      </span>
                      <span className="rounded-md border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-bold text-indigo-900">
                        {item.category}
                      </span>
                      {item.is_pinned && (
                        <span className="rounded-md bg-rose-100 border border-rose-300 px-2 py-0.5 text-[10px] font-extrabold text-rose-900">
                          PINNED PRIORITY
                        </span>
                      )}
                    </div>
                    <h4 className="text-sm font-extrabold text-slate-900">{item.title}</h4>
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5">
                    <button
                      type="button"
                      data-testid={`copy-continue-prompt-${(item.ref_code || '').replace(/[^A-Z0-9-]/gi, '')}`}
                      onClick={() => void handleCopyText(item.ref_code, item.how_to_continue_prompt)}
                      className="inline-flex items-center gap-1 rounded-lg bg-indigo-700 hover:bg-indigo-600 px-2.5 py-1 text-[11px] font-bold text-white"
                    >
                      {copiedCode === item.ref_code ? (
                        <>
                          <Check className="h-3.5 w-3.5" />
                          Copied Continue Prompt!
                        </>
                      ) : (
                        <>
                          <Copy className="h-3.5 w-3.5" />
                          Copy &ldquo;Continue {item.ref_code}&rdquo; Prompt
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <p className="text-slate-700 leading-relaxed">{item.plain_english_summary}</p>

                {item.five_point_client_recap && (
                  <div className="rounded-lg border border-indigo-200 bg-indigo-50/50 p-2.5 grid grid-cols-1 gap-2 sm:grid-cols-5 text-[11px]">
                    <div>
                      <span className="font-extrabold text-indigo-950 block">1. Who &amp; Relationship</span>
                      <span className="text-slate-700">{item.five_point_client_recap.who_and_relationship}</span>
                    </div>
                    <div>
                      <span className="font-extrabold text-indigo-950 block">2. Did We Meet/Speak?</span>
                      <span className="text-slate-700">{item.five_point_client_recap.did_we_meet_or_speak}</span>
                    </div>
                    <div>
                      <span className="font-extrabold text-indigo-950 block">3. The Situation</span>
                      <span className="text-slate-700">{item.five_point_client_recap.the_situation}</span>
                    </div>
                    <div>
                      <span className="font-extrabold text-indigo-950 block">4. Discussed &amp; Promised</span>
                      <span className="text-slate-700">{item.five_point_client_recap.discussed_and_promised}</span>
                    </div>
                    <div>
                      <span className="font-extrabold text-rose-900 block">5. Action Right Now</span>
                      <span className="text-slate-900 font-semibold">
                        {item.five_point_client_recap.what_needs_to_be_done_now}
                      </span>
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t border-slate-100">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {Array.isArray(item.platform_urls) &&
                      item.platform_urls.map((u, idx) => {
                        const rawUrl = typeof u === 'string' ? u : u?.url || '';
                        const rawLabel =
                          typeof u === 'string'
                            ? rawUrl.replace('https://platform.propellertravel.com', '') || rawUrl
                            : u?.label || rawUrl.replace('https://platform.propellertravel.com', '') || 'Open Link';
                        if (!rawUrl) return null;
                        return (
                          <Link
                            key={idx}
                            to={rawUrl.replace('https://platform.propellertravel.com', '')}
                            className="rounded border border-slate-200 bg-slate-50 hover:bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-900"
                          >
                            {rawLabel} →
                          </Link>
                        );
                      })}
                  </div>
                  <div className="font-mono text-[11px] text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                    <strong>How to continue in chat:</strong> {item.how_to_continue_prompt}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default RealityCheckCopilotPanel;
